import { HttpClient } from '@angular/common/http';
import { computed, Injectable, signal } from '@angular/core';
import { catchError, Observable, of, tap } from 'rxjs';
import { EligibilitySession } from '../models/eligibility-session';
import { PaginatedResponse } from '../models/paginated-response';
import { EligibilitySite } from '../models/eligibility-site';
import { CreateSessionBody } from '../models/create-session-body';
import { environment } from '../../environments/environment';
import { EligibilityFile } from '../models/eligibility-file';
import { TechnicalEligibilityRequestResponse } from '../models/technical-eligbility-request-response';
import { TechnicalEligibilityRequestBody } from '../models/technical-eligbility-request-body';
import { TechnicalEligibilityBulkRequest, TechnicalEligibilityBulkRequestExtra, TechnicalEligibilitySiteRequest } from '../models/technical-eligibility-bulk-request';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  constructor(private readonly http: HttpClient) {}

  /* Cached data */

  public readonly currentSession = signal<EligibilitySession | null>(null);
  public readonly currentSessionSites = signal<EligibilitySite[]>([]);

  public readonly currentTechnicalElibilityBulkRequest = signal<TechnicalEligibilityBulkRequest | null>(null);
  public readonly currentTechnicalElibilityBulkRequestId = computed<number | null>(() => {
    const currentTechnicalElibilityBulkRequest = this.currentTechnicalElibilityBulkRequest();
    return currentTechnicalElibilityBulkRequest !== null ? currentTechnicalElibilityBulkRequest.id : null;
  })

  public readonly currentSessionCode = computed<string>(() => {
    const currentSession = this.currentSession();
    return currentSession ? currentSession.code : '';
  });

  public readonly hasCurrentSession = computed<boolean>(() => {
    const currentSession = this.currentSession();
    const currentSessionCode = this.currentSessionCode();

    // By returning directly the condition below TypeScript says it is of type boolean or null instead of just boolean
    if (currentSession && currentSessionCode !== '') {
      return true;
    }
    return false;
  })

  public readonly areCurrentSessionSitesValidated = computed<boolean>(() => {
    return this.currentSessionSites().length > 0 
      && this.currentSessionSites().filter(site => !site.validatedAddressState).length === 0;
  });

  resetCurrentSession(): void {
    this.currentSession.set(null);
    this.currentSessionSites.set([]);
  }

  updateCurrentSessionSites(sites: EligibilitySite[]): void {
    this.currentSessionSites.set(sites);
  }

  deleteCurrentSessionSite(siteId: number): void {
    const currentSessionSites = this.currentSessionSites().filter(site => site.id !== siteId);
    this.currentSessionSites.set(currentSessionSites);
  }

  addCachedSessionSite(site: EligibilitySite): void {
    const currentSessionSites = this.currentSessionSites();
    this.currentSessionSites.set([...currentSessionSites, site]);
  }

  /* Update a cached session site present inside the current session sites list, to update headers and list without retrieving the whole sites list again */
  updateCachedSessionSite(updatedSite: EligibilitySite) {
    const newSites = this.currentSessionSites().map((site) => {
      if (site.id === updatedSite.id) {
        return updatedSite;
      } else {
        return site;
      }
    });
    this.currentSessionSites.set(newSites);
  }

  /* Session */

  getAllSessions(displayArchived = false): Observable<PaginatedResponse<EligibilitySession>> {
    return this.http.get<PaginatedResponse<EligibilitySession>>(`${environment.backendBaseUrl}/sessions`, {
      params: {
        limit: 100,
        display_archived: displayArchived
      }
    });
  }

  createSession(name: string): Observable<EligibilitySession> {
    let body: CreateSessionBody = { name: name };
    return this.http.post<EligibilitySession>(`${environment.backendBaseUrl}/sessions`, body);
  }

  duplicateSession(sessionCode: string): Observable<EligibilitySession> {
    return this.http.post<EligibilitySession>(`${environment.backendBaseUrl}/sessions/${sessionCode}/duplicate`, null);
  }
  
  getSession(sessionCode: string): Observable<EligibilitySession> {
    const currentSession = this.currentSession();
    // There is cached data for current session
    if (sessionCode === this.currentSessionCode() && currentSession !== null) {
      return of(currentSession);
    }
    
    // Otherwise, retrieve data normally
    return this.http.get<EligibilitySession>(`${environment.backendBaseUrl}/sessions/${sessionCode}`)
      .pipe(
        tap((session) => {
          this.currentSession.set(session);
        }),
        catchError(() => {
          this.resetCurrentSession();
          throw new Error(`Could not retrieve session ${sessionCode}`);
        }) 
      );
  }

  updateSession(code: string, body: any): Observable<EligibilitySession> {
    return this.http.patch<EligibilitySession>(`${environment.backendBaseUrl}/sessions/${code}`, body);
  }

  archiveSession(sessionCode: string, archived: boolean): Observable<void> {
    return this.http.patch<void>(`${environment.backendBaseUrl}/sessions/${sessionCode}/archive`, { archived: archived });
  }

  deleteSession(sessionCode: string): Observable<void> {
    return this.http.delete<void>(`${environment.backendBaseUrl}/sessions/${sessionCode}`);
  }

  launchSessionSitesFiab(sessionCode: string, siteIds: number[]): Observable<void> {
    return this.http.post<void>(`${environment.backendBaseUrl}/sessions/${sessionCode}/sites/fiab`, siteIds);
  }

  /* Session sites */

  getSessionSites(sessionCode: string, reloadIfPresent: boolean = false, includeLatestSiteRequest = false): Observable<PaginatedResponse<EligibilitySite>> {
    const currentSessionSites = this.currentSessionSites();
    // We have cached data for session sites
    if (reloadIfPresent === false && sessionCode === this.currentSessionCode() && currentSessionSites.length > 0) {
      const paginatedResponse = {
        itemCount: currentSessionSites.length,
        totalItemCount: currentSessionSites.length,
        items: currentSessionSites
      }
      return of(paginatedResponse);
    } 
    
    // Otherwise, retrieve data normally
    return this.http.get<PaginatedResponse<EligibilitySite>>(`${environment.backendBaseUrl}/sessions/${sessionCode}/sites`, {
      params: {
        limit: 1000,
        include_latest_site_request: includeLatestSiteRequest
      }
    }).pipe(
      tap((response) => {
        if (sessionCode === this.currentSessionCode()) {
          this.currentSessionSites.set(response.items);
        }
      }),
      catchError(() => {
        if (sessionCode === this.currentSessionCode()) {
          this.currentSessionSites.set([]);
        }
        throw new Error(`Could not retrieve sites for session ${sessionCode}`);
      })
    );
  }

  getSessionSite(sessionCode: string, siteId: number): Observable<EligibilitySite> {
    return this.http.get<EligibilitySite>(`${environment.backendBaseUrl}/sessions/${sessionCode}/sites/${siteId}`)
      .pipe(
        tap((response: EligibilitySite) => {
          if (sessionCode === this.currentSessionCode()) {
            this.updateCachedSessionSite(response);
          }
        }),
        catchError(() => {
          throw new Error(`Could not retrieve site ${siteId} for session ${sessionCode}`);
        })
      );
  }

  deleteSessionSite(sessionCode: string, siteId: number): Observable<void> {
    return this.http.delete<void>(`${environment.backendBaseUrl}/sessions/${sessionCode}/sites/${siteId}`)
      .pipe(
        tap(() => {
          if (sessionCode === this.currentSessionCode()) {
            const newSessionSites = this.currentSessionSites().filter((site: EligibilitySite) => site.id !== siteId);
            this.currentSessionSites.set(newSessionSites);
          }
        }),
        catchError(() => {
          throw new Error(`Could not update site ${siteId} for session ${sessionCode}`);
        })
      );
  }

  deleteSessionSites(sessionCode: string, siteIds: number[]): Observable<void> {
    return this.http.delete<void>(`${environment.backendBaseUrl}/sessions/${sessionCode}/sites`, {
      body: siteIds
    });
  }

  createSessionSite(sessionCode: string, siteCreate: any): Observable<EligibilitySite> {
    return this.http.post<EligibilitySite>(`${environment.backendBaseUrl}/sessions/${sessionCode}/sites`, siteCreate)
      .pipe(
        tap((response: EligibilitySite) => {
          if (sessionCode === this.currentSessionCode()) {
            this.addCachedSessionSite(response);
          }
        }),
        catchError(() => {
          throw new Error(`Could not create site for session ${sessionCode}`);
        })
      );
  }
  
  updateSessionSite(sessionCode: string, siteId: number, siteUpdate: any, excludeNone: boolean = true): Observable<EligibilitySite> {
    return this.http.patch<EligibilitySite>(`${environment.backendBaseUrl}/sessions/${sessionCode}/sites/${siteId}?excludeNone=${excludeNone}`, siteUpdate)
      .pipe(
        tap((response: EligibilitySite) => {
          if (sessionCode === this.currentSessionCode()) {
            this.updateCachedSessionSite(response);
          }
        }),
        catchError(() => {
          throw new Error(`Could not update site ${siteId} for session ${sessionCode}`);
        })
      );
  }

  getSessionSiteWithLatestSiteRequest(sessionCode: string, siteId: number): Observable<EligibilitySite> {
    return this.http.get<EligibilitySite>(`${environment.backendBaseUrl}/sessions/${sessionCode}/sites/${siteId}`, {
      params: {
        include_latest_site_request: true
      }
    })
      .pipe(
        tap((response: EligibilitySite) => {
          if (sessionCode === this.currentSessionCode()) {
            this.updateCachedSessionSite(response);
          }
        }),
        catchError(() => {
          throw new Error(`Could not retrieve site ${siteId} for session ${sessionCode}`);
        })
      );
  }

  launchSessionSiteFiab(sessionCode: string, siteId: number): Observable<EligibilitySite> {
    return this.http.post<EligibilitySite>(`${environment.backendBaseUrl}/sessions/${sessionCode}/sites/${siteId}/fiab`, null)
      .pipe(
        tap((response: EligibilitySite) => {
          if (sessionCode === this.currentSessionCode()) {
            this.updateCachedSessionSite(response);
          }
        }),
        catchError(() => {
          throw new Error(`Could not launch site validation for site ${siteId}, session ${sessionCode}`);
        })
      );
  }

  exportSessionSites(sessionCode: string, siteIds: number[]) {
    return this.http.post(`${environment.backendBaseUrl}/sessions/${sessionCode}/sites/export`, siteIds, {responseType: 'blob' as 'json'});
  }

  /* Session files */

  getSessionFiles(code: string): Observable<EligibilityFile[]> {
    return this.http.get<EligibilityFile[]>(`${environment.backendBaseUrl}/sessions/${code}/files`);
  }

  /* Session technical eligibility */

  requestTechnicalEligibility(sessionCode: string, providers: string[], technologies: string[], siteIds: number[]): Observable<TechnicalEligibilityRequestResponse> {
    const body: TechnicalEligibilityRequestBody = {
      site_ids: siteIds,
      providers: providers,
      technologies: technologies
    }
    return this.http.post<TechnicalEligibilityRequestResponse>(`${environment.backendBaseUrl}/sessions/${sessionCode}/sites/technicalEligibility`, body);
  }

  getSessionTechnicalEligibilityBulkRequests(sessionCode: string): Observable<PaginatedResponse<TechnicalEligibilityBulkRequest>> {
    return this.http.get<PaginatedResponse<TechnicalEligibilityBulkRequest>>(`${environment.backendBaseUrl}/sessions/${sessionCode}/technicalEligibilityBulkRequests`);
  }

  getSessionTechnicalEligibilityBulkRequest(sessionCode: string, technicalEligibilityBulkRequestId: number): Observable<TechnicalEligibilityBulkRequestExtra> {
    return this.http.get<TechnicalEligibilityBulkRequestExtra>(`${environment.backendBaseUrl}/sessions/${sessionCode}/technicalEligibilityBulkRequests/${technicalEligibilityBulkRequestId}`)
      .pipe(
        tap((response: any) => {
          this.currentTechnicalElibilityBulkRequest.set(response);
        })
      );
  }

  getSessionTechnicalEligibilityBulkRequestSiteRequest(sessionCode: string, technicalEligibilityBulkRequestId: number, siteId: number): Observable<TechnicalEligibilitySiteRequest> {
    return this.http.get<TechnicalEligibilitySiteRequest>(`${environment.backendBaseUrl}/sessions/${sessionCode}/technicalEligibilityBulkRequests/${technicalEligibilityBulkRequestId}/sites/${siteId}`);
  }

  getSessionTechnicalEligibilityBulkRequestSiteRequests(sessionCode: string, technicalEligibilityBulkRequestId: number): Observable<TechnicalEligibilitySiteRequest[]> {
    return this.http.get<TechnicalEligibilitySiteRequest[]>(`${environment.backendBaseUrl}/sessions/${sessionCode}/technicalEligibilityBulkRequests/${technicalEligibilityBulkRequestId}/sites`);
  }

  getSessionTechnicalEligbilityBulkRequestSiteProviderResponse(sessionCode: string, technicalEligibilityBulkRequestId: number, siteId: number, provider: string) {
    return this.http.get<any>(`${environment.backendBaseUrl}/sessions/${sessionCode}/technicalEligibilityBulkRequests/${technicalEligibilityBulkRequestId}/sites/${siteId}/providers/${provider}`);
  }
}
